import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useCopyLink } from "@/services/hooks/useCopyLink";
import { DetailedViewOptions } from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import DeleteEntityModal from "@/components/layouts/DeleteEntityModal";
import ToastBody from "@/components/ui/toast-body";
import CreateLeadModal from "@/pages/leads/components/CreateLeadModal";
import CreateDealModal from "@/pages/deals/components/CreateDealModal";
import CreateContractModal from "@/pages/contracts/components/CreateContractModal";
import CreatePersonModal from "@/components/layouts/CreatePersonModal";
import {
  EntityType,
  useEntityType
} from "@/services/hooks/entities/useEntityType";
import { CompanyData, CompanyStatus } from "@/pages/companies/types";
import { useResetFilteredDeals } from "@/pages/deals/hooks";
import { useResetLeadsInfinite } from "@/pages/leads/hooks";

export const useCompanyActions = (
  companyEntity: CompanyData | undefined,
  forceRefetch?: boolean,
  includeAdditionals?: boolean
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleCopyLink = useCopyLink();

  const {
    company,
    setCompany,
    resetCompanies,
    onUpdateCompanyField: onUpdateField,
    deleteCompany
  } = useEntityType(
    EntityType.COMPANY,
    companyEntity,
    false,
    undefined,
    undefined,
    forceRefetch,
    includeAdditionals
  );

  const updateCompanyState = useCallback(
    (updatedFields: Partial<CompanyData>) => {
      if (company) {
        setCompany({
          ...company,
          ...updatedFields
        });
      }
    },
    [company, setCompany]
  );

  const resetFilteredDeals = useResetFilteredDeals();
  const resetLeadsInfinite = useResetLeadsInfinite();

  const [isOpenDeleteCompanyModal, setIsOpenDeleteCompanyModal] =
    useState(false);
  const [isTasksDrawerOpen, setIsTasksDrawerOpen] = useState(false);
  const [isCreateLeadModalOpen, setIsCreateLeadModalOpen] = useState(false);
  const [isCreateDealModalOpen, setIsCreateDealModalOpen] = useState(false);
  const [isCreateContractModalOpen, setIsCreateContractModalOpen] =
    useState(false);
  const [isCreatePersonModalOpen, setIsCreatePersonModalOpen] = useState(false);

  const toggleTasksDrawer = () => setIsTasksDrawerOpen(!isTasksDrawerOpen);


  const handleDelete = (
    setIsOpenDeleteCompanyModal: Dispatch<SetStateAction<boolean>>,
    isQuickView: boolean = false,
    closeDrawer?: () => void
  ) => {
    if (company) {
      deleteCompany.mutate(
        {
          pathParams: { companyId: company.id }
        },
        {
          onSuccess: () => {
            if (isQuickView) {
              setIsOpenDeleteCompanyModal(false);
              closeDrawer?.();
            } else {
              navigate("/companies", { replace: true });
            }
            resetCompanies();
            toast.success(
              <ToastBody title={`${t("companies.companyView.toast1")} ${company?.name || ""} ${t("companies.companyView.toast2")}`} />,
              {
                duration: 5000,
                icon: null
              }
            );
          }
        }
      );
    }
  };

  const handleStatusUpdate = (options: {
    status: CompanyStatus;
    successToastText?: string;
    onSuccessFn?: () => void;
  }) =>
    onUpdateField(
      "status",
      options.status,
      undefined,
      options?.successToastText,
      options?.onSuccessFn
    );

  const getCreateOptions = () => {
    return {
      lead: () => setIsCreateLeadModalOpen(true),
      deal: () => setIsCreateDealModalOpen(true),
      contract: () => setIsCreateContractModalOpen(true),
      person: () => setIsCreatePersonModalOpen(true)
    } as DetailedViewOptions;
  };
  const getTopOptions = () => {
    const isActiveCustomer = company?.status === CompanyStatus.ACTIVE_CUSTOMER;
    const isOnHold = company?.status === CompanyStatus.ON_HOLD_FOR_PROSPECTING;
    const isProspect = company?.status === CompanyStatus.PROSPECT;
    const isChurned = company?.status === CompanyStatus.CHURNED;
    const isDisqualified = company?.status === CompanyStatus.DISQUALIFIED;
    return {
      ...((isProspect || isChurned) && {
        markAsAnActiveCustomer: () => {
          handleStatusUpdate({
            status: CompanyStatus.ACTIVE_CUSTOMER,
            successToastText: t("companies.companyView.activeCustomerToast")
          });
        }
      }),
      ...(isProspect && {
        holdForAEProspecting: () => {
          handleStatusUpdate({
            status: CompanyStatus.ON_HOLD_FOR_PROSPECTING,
            successToastText: t(
              "companies.companyView.holdForAEProspectingToast"
            )
          });
        }
      }),
      ...(isActiveCustomer && {
        markAsChurned: () => {
          handleStatusUpdate({
            status: CompanyStatus.CHURNED,
            successToastText: t("companies.companyView.markAsChurnedToast")
          });
        }
      }),
      ...((isOnHold || isProspect || isChurned) && {
        disqualifyCompany: () => {
          handleStatusUpdate({
            status: CompanyStatus.DISQUALIFIED,
            successToastText: t("companies.companyView.disqualifyToast")
          });
        }
      }),
      ...((isChurned || isActiveCustomer) && {
        markAsProspect: () => {
          handleStatusUpdate({
            status: CompanyStatus.PROSPECT,
            successToastText: t("companies.companyView.prospectToast")
          });
        }
      }),
      ...(isDisqualified && {
        requalifyCompany: () => {
          handleStatusUpdate({
            status: CompanyStatus.PROSPECT,
            successToastText: t("companies.companyView.prospectToast")
          });
        }
      })
    } as DetailedViewOptions;
  };

  const getOptions = (isQuickView: boolean = false, companyId?: string) => {
    return {
      copyLink: () => handleCopyLink(isQuickView, companyId),
      companyLog: () =>
        isQuickView
          ? navigate(`/companies/${companyId}/log`, { replace: true })
          : navigate("log"),
      delete: {
        action: () => setIsOpenDeleteCompanyModal(true),
        variant: "destructive"
      }
    } as DetailedViewOptions;
  };

  const handleCreateSuccess = () => {
    // Reset deals and leads data to refresh the InvolvedIn section
    resetFilteredDeals();
    resetLeadsInfinite();
  };

  const renderModals = (
    isDropdownClosing = false,
    isQuickView = false,
    onClose?: () => void,
    userData?: any
  ) => {
    return (
      <>
        <DeleteEntityModal
          isOpen={isOpenDeleteCompanyModal && !isDropdownClosing}
          setIsOpen={setIsOpenDeleteCompanyModal}
          entity={EntityType.COMPANY}
          entityName={company?.name || ""}
          handleDelete={() =>
            handleDelete(setIsOpenDeleteCompanyModal, isQuickView, onClose)
          }
        />
        {userData && (
          <>
            <CreateLeadModal
              isOpen={isCreateLeadModalOpen && !isDropdownClosing}
              setIsOpen={setIsCreateLeadModalOpen}
              userData={userData}
              resetPage={handleCreateSuccess}
              companyId={company?.id}
            />
            <CreateDealModal
              isOpen={isCreateDealModalOpen && !isDropdownClosing}
              setIsOpen={setIsCreateDealModalOpen}
              userData={userData}
              onDealCreated={handleCreateSuccess}
              companyId={company?.id}
            />
            <CreateContractModal
              isOpen={isCreateContractModalOpen && !isDropdownClosing}
              setIsOpen={setIsCreateContractModalOpen}
              userData={userData}
              onContractCreated={handleCreateSuccess}
              companyId={company?.id}
              aeOwner={company?.aeOwner ?? undefined}
              csmOwner={company?.csmOwner ?? undefined}
            />
            <CreatePersonModal
              isOpen={isCreatePersonModalOpen && !isDropdownClosing}
              setIsOpen={setIsCreatePersonModalOpen}
              resetPage={handleCreateSuccess}
              companyId={company?.id}
            />
          </>
        )}
      </>
    );
  };

  return {
    company,
    updateCompanyState,
    isTasksDrawerOpen,
    toggleTasksDrawer,
    onUpdateField,
    getTopOptions,
    getCreateOptions,
    getOptions,
    renderModals
  };
};
